/**
 * settings/settingsComponents.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Small reusable components used inside SettingsTab:
 *
 *   ResetPasswordModal  — password change form with email verification
 *   IdUploader          — file input + preview for ID document uploads
 *   RestoreConfirmModal — confirms restoring profile to a previous snapshot
 *
 * All exported as named exports and imported by SettingsTab.jsx.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useRef, useEffect } from 'react';
import styles from './SettingsTab.module.css';
import apiService from '../../../services/api';
import { useAuthContext } from '../../../context/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────
const VERDICTS = [
  { value: 'verified',              label: '✅ Account Verified', bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  { value: 'pending',               label: '⏳ Under Review',     bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
  { value: 'pending_reverification',label: '🔄 Re-verification',  bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
  { value: 'partial',               label: '~ Partial',           bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
  { value: 'rejected',              label: '🚫 Rejected',         bg: '#fdf2f8', color: '#9333ea', border: '#d8b4fe' },
  { value: 'unverified',            label: '✗ Not Verified',      bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
];

// ─── Reset Password Modal ─────────────────────────────────────────────────────

// const VERDICTS = ['pending', 'verified', 'rejected', 'reverify'];

export function ResetPasswordModal({ userEmail, onClose }) {
  const [step, setStep] = useState('confirm');

  const handleSend = async () => {
    setStep('sending');
    try {
      await apiService.requestPasswordReset(userEmail);
      setStep('sent');
    } catch {
      setStep('confirm');
      alert('Failed to send reset email. Please try again.');
    }
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.resetModal} onClick={e => e.stopPropagation()}>
        <button className={styles.modalCloseBtn} onClick={onClose}>×</button>

        {step === 'sent' ? (
          <div className={styles.resetSent}>
            <div className={styles.resetSentIcon}>✅</div>
            <h3 className={styles.resetTitle}>Check Your Email</h3>
            <p className={styles.resetBody}>
              A password reset link has been sent to <strong>{userEmail}</strong>.
              Follow the link to set a new password.
            </p>
            <button className={styles.btnPrimary} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className={styles.resetIconWrap}>🔑</div>
            <h3 className={styles.resetTitle}>Reset Password</h3>
            <p className={styles.resetBody}>
              We'll send a password reset link to your registered email address.
              You'll be able to set a new password from there.
            </p>
            <div className={styles.resetEmailPreview}>{userEmail}</div>
            <div className={styles.resetActions}>
              <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
              <button
                className={styles.btnPrimary}
                onClick={handleSend}
                disabled={step === 'sending'}
              >
                {step === 'sending' ? <><span className={styles.spinner} /> Sending…</> : 'Send Reset Link'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ID Uploader ──────────────────────────────────────────────────────────────

export function IdUploader({ label, value, onSet, fieldName }) {
  const ref = useRef();
  const { user } = useAuthContext();
  const [removing, setRemoving] = useState(false);

  const handleChange = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => onSet(ev.target.result, file);
    r.readAsDataURL(file);
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      // Tell the backend to delete from Supabase + null out the DB field
      const token =
        localStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('authToken') || '';
      const fd = new FormData();
      fd.append(fieldName, ''); // empty string = "delete this field"
      await fetch(
        `${(process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api')}/auth/profile/update/`,
        { method: 'PATCH', headers: { Authorization: `Token ${token}` }, body: fd }
      );
    } catch {
      // Non-fatal — still clear locally even if network fails
    } finally {
      setRemoving(false);
      onSet(null, null); // clear local preview state
    }
  };

  return (
    <div>
      <div className={styles.idLabel}>{label}</div>
      <div
        className={styles.idBox}
        onClick={() => ref.current?.click()}
        role="button" tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && ref.current?.click()}
      >
        {value ? (
          <img src={value} alt={label} className={styles.idPreviewImg} />
        ) : (
          <div className={styles.idPlaceholder}>
            <span className={styles.idIcon}>🪪</span>
            <span className={styles.idClickText}>Click to upload</span>
            <span className={styles.idFormats}>JPG, PNG, PDF</span>
          </div>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleChange} />
      {value && (
        <button
          className={styles.idRemoveBtn}
          onClick={handleRemove}
          disabled={removing}
        >
          {removing ? '⏳ Removing…' : '✕ Remove'}
        </button>
      )}
    </div>
  );
}

// ─── Restore Confirmation Modal ───────────────────────────────────────────────

export function RestoreConfirmModal({ snapshot, onConfirm, onClose }) {
  const fields = [
    { label: 'First Name',     value: snapshot?.first_name    || '—' },
    { label: 'Last Name',      value: snapshot?.last_name     || '—' },
    { label: 'Phone',          value: snapshot?.phone         || '—' },
    { label: 'Location',       value: snapshot?.location      || '—' },
    { label: 'Business Name',  value: snapshot?.business_name || '—' },
  ].filter(f => f.value !== '—');

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.resetModal} onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <button className={styles.modalCloseBtn} onClick={onClose}>×</button>
        <div className={styles.resetIconWrap}>↩️</div>
        <h3 className={styles.resetTitle}>Restore Previous Details?</h3>
        <p className={styles.resetBody}>
          This will reset your profile back to the details that were verified before your recent changes.
        </p>
        <div style={{
          background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '10px 14px', marginBottom: 20, textAlign: 'left',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase',
                        letterSpacing: '.4px', marginBottom: 8 }}>
            Will be restored to
          </div>
          {fields.map(f => (
            <div key={f.label} style={{
              display: 'flex', justifyContent: 'space-between', fontSize: 13,
              padding: '4px 0', borderBottom: '1px solid #f1f5f9',
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>{f.label}</span>
              <span style={{ color: '#111', fontWeight: 700 }}>{f.value}</span>
            </div>
          ))}
        </div>
        <div className={styles.resetActions}>
          <button className={styles.btnCancel} onClick={onClose}>Keep My Changes</button>
          <button className={styles.btnPrimary} onClick={onConfirm}>
            ↩️ Restore
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
